import { BaseService } from './base.service';
import { CustomResponseEntity } from '../types/common';
import { ChallengeDto } from '../types/dtos/challenge';
import { api } from '../client/axiosClient';
import {
  CreateChallengeDto,
  ChallengeTableResponse,
  ChallengesTableParams,
  PaginatedChallengeTableResponse,
  ChallengesByOrganizationResponse,
  SyncProgramVersionItemsResult,
  BatchSyncResult,
  AddOrgsToChallengeDto,
  AddOrgsResultDto,
  MergePreviewRequestDto,
  MergePreviewResponseDto,
  MergeRequestDto,
  MergeResultDto,
  ChallengeTemplateDto,
} from '../types/dtos/challenge/ChallengeDto';

/**
 * Challenge Service handles CRUD operations for challenges (inspections).
 *
 * URL Pattern: /api/v1/challenges
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId constructor parameter has been removed from URL paths.
 * The backend extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId constructor parameter is deprecated and no longer used.
 */
const BASE_PATH = '/api/v1/challenges';

export class ChallengeService extends BaseService<ChallengeDto> {
  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   */
  constructor(_organizationId: string) {
    super(BASE_PATH);
  }
}

export const createChallengeService = (_organizationId: string) => new ChallengeService(_organizationId);

/**
 * Create a new challenge.
 * @param data - Challenge data including organizationId (deprecated - org context from header)
 * @throws Error if organizationId is not provided (for backward compatibility with existing code)
 */
export const createChallenge = async (data: CreateChallengeDto & { organizationId: string }): Promise<CustomResponseEntity<any>> => {
  // Validate organizationId is provided (backward compatibility check)
  if (!data.organizationId) {
    throw new Error('Organization ID is required');
  }
  const { organizationId: _orgId, ...challengeData } = data;
  // Organization context comes from X-Organization-Id header
  return api.post<CustomResponseEntity<any>>(BASE_PATH, challengeData);
};

/**
 * Get challenges table data (non-paginated - legacy)
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @deprecated Use getChallengesTablePaginated instead
 */
export const getChallengesTable = async (_organizationId: string): Promise<ChallengeTableResponse> => {
  const response = await api.get<ChallengeTableResponse>(`${BASE_PATH}/table`);
  return response as unknown as ChallengeTableResponse;
};

/**
 * Get challenges table data with pagination and search support
 * 
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param params - Pagination and search parameters
 * @returns Paginated challenge table response
 */
export const getChallengesTablePaginated = async (
  _organizationId: string,
  params: ChallengesTableParams = {}
): Promise<PaginatedChallengeTableResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }
  if (params.size !== undefined) {
    queryParams.append('size', params.size.toString());
  }
  if (params.searchTerm) {
    queryParams.append('searchTerm', params.searchTerm);
  }
  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }
  if (params.sortDirection) {
    queryParams.append('sortDirection', params.sortDirection);
  }
  
  const queryString = queryParams.toString();
  const url = `${BASE_PATH}/table${queryString ? `?${queryString}` : ''}`;
  
  const response = await api.get<PaginatedChallengeTableResponse>(url);
  return response as unknown as PaginatedChallengeTableResponse;
};

/**
 * Get challenge data for a specific organization (for FarmDetailsModal)
 * Fetches only the challenge data for the specified inspected organization
 * 
 * @param _parentOrgId - Deprecated: Parent organization ID (no longer used, kept for backward compatibility)
 * @param inspectedOrgId - The organization/farm to get challenge data for
 * @returns Challenge data for the specific organization
 */
export const getChallengesByOrganization = async (
  _parentOrgId: string,
  inspectedOrgId: string
): Promise<ChallengesByOrganizationResponse> => {
  const response = await api.get<ChallengesByOrganizationResponse>(
    `${BASE_PATH}/table/organization/${inspectedOrgId}`
  );
  return response as unknown as ChallengesByOrganizationResponse;
};

/**
 * Sync new ProgramVersionItems to an existing challenge.
 * Creates InspectionsProgramsItems for any new PVIs that were added to the program version
 * after the challenge was created. Never modifies or deletes existing IPVI.
 * 
 * All new IPVI are created with isActive=false (pending state).
 * 
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param challengeId - The challenge (inspection) ID to update
 * @param programVersionId - The program version ID to sync items from
 * @returns Sync result with counts of created, skipped, and errors
 */
export const syncChallengeProgramVersionItems = async (
  _organizationId: string,
  challengeId: string,
  programVersionId: string
): Promise<CustomResponseEntity<SyncProgramVersionItemsResult>> => {
  const response = await api.post<CustomResponseEntity<SyncProgramVersionItemsResult>>(
    `${BASE_PATH}/${challengeId}/program-versions/${programVersionId}/sync-items`
  );
  return response as unknown as CustomResponseEntity<SyncProgramVersionItemsResult>;
};

/**
 * Batch sync new ProgramVersionItems across multiple organizations/farms.
 * For each farm, finds all challenges that contain the specified program version
 * and creates InspectionsProgramsItems for any new PVIs.
 * 
 * All new IPVI are created with isActive=false (pending state).
 * 
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param programVersionId - The program version ID to sync items from
 * @param inspectedOrgIds - Array of organization/farm IDs to update
 * @returns Batch sync result with total counts and per-farm results
 */
export const batchSyncProgramVersionItems = async (
  _organizationId: string,
  programVersionId: string,
  inspectedOrgIds: string[]
): Promise<CustomResponseEntity<BatchSyncResult>> => {
  const response = await api.post<CustomResponseEntity<BatchSyncResult>>(
    `${BASE_PATH}/batch-sync-items`,
    {
      programVersionId,
      inspectedOrgIds
    },
    { timeout: 300000 } // 5 minutes timeout for large batch operations
  );
  return response as unknown as CustomResponseEntity<BatchSyncResult>;
};

/**
 * Add organizations to an existing challenge.
 * Copies the challenge configuration (name, description, program versions, etc.) from
 * the specified challenge and creates new Inspection entities for each organization.
 *
 * @param challengeId - The existing challenge ID to copy configuration from
 * @param inspectedOrgIds - Array of organization/farm IDs to add
 * @returns Result with counts of created, skipped, and skipped org names
 */
export const addOrganizationsToChallenge = async (
  challengeId: string,
  inspectedOrgIds: string[]
): Promise<CustomResponseEntity<AddOrgsResultDto>> => {
  const response = await api.post<CustomResponseEntity<AddOrgsResultDto>>(
    `${BASE_PATH}/${challengeId}/add-organizations`,
    { inspectedOrgIds } as AddOrgsToChallengeDto
  );
  return response as unknown as CustomResponseEntity<AddOrgsResultDto>;
};

/**
 * Preview a challenge merge. Shows conflicts and counts before executing.
 */
export const previewMergeChallenges = async (
  payload: MergePreviewRequestDto
): Promise<CustomResponseEntity<MergePreviewResponseDto>> => {
  const response = await api.post<CustomResponseEntity<MergePreviewResponseDto>>(
    `${BASE_PATH}/merge-preview`,
    payload
  );
  return response as unknown as CustomResponseEntity<MergePreviewResponseDto>;
};

/**
 * Execute a challenge merge. Renames source inspections to the target challenge name.
 */
export const mergeChallenges = async (
  payload: MergeRequestDto
): Promise<CustomResponseEntity<MergeResultDto>> => {
  const response = await api.post<CustomResponseEntity<MergeResultDto>>(
    `${BASE_PATH}/merge`,
    payload
  );
  return response as unknown as CustomResponseEntity<MergeResultDto>;
};

/**
 * Get challenge templates grouped by name with organization counts.
 * Runs a GROUP BY query on the backend instead of loading full inspection data.
 */
export const getChallengeTemplates = async (): Promise<
  CustomResponseEntity<ChallengeTemplateDto[]>
> => {
  const response = await api.get<CustomResponseEntity<ChallengeTemplateDto[]>>(
    `${BASE_PATH}/templates`
  );
  return response as unknown as CustomResponseEntity<ChallengeTemplateDto[]>;
};